import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import GamifiedProgress from './GamifiedProgress';
import HeroCompanion from './HeroCompanion';
import HeroOfTheDayCard from './HeroOfTheDayCard';
import StatTile from './StatTile';
import StreakBadge from './StreakBadge';
import { useTranslation } from 'react-i18next';
import { HEROES, HERO_ORDER } from '../data/heroes';
import { ACTIVITY_CATALOG } from '../data/activityCatalog';
import {
  Flame,
  BookOpen,
  Heart,
  Sparkles,
  Wind,
  Crown,
  Mic,
  Target,
  TrendingUp,
  Compass,
  Shield,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const HERO_ICONS = { queen: Crown, victor: Mic, cam: Wind };
const HERO_QUICK_LABEL_KEY = {
  queen: 'heroLed.affirmNow',
  victor: 'heroLed.speakNow',
  cam: 'heroLed.breatheNow',
};

const YouthDashboard = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const [dashboardData, setDashboardData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dailyGoal, setDailyGoal] = useState({ target: 3, completed: 0 });
  const [streakDismissed, setStreakDismissed] = useState(false);

  const motivationalMessages = t('youth.motivational', { returnObjects: true }) || [];
  const [currentMessage] = useState(
    motivationalMessages[Math.floor(Math.random() * (motivationalMessages.length || 1))] || ''
  );

  useEffect(() => {
    fetchDashboardData();
    fetchRecommendations();
    axios
      .get('/auth/me')
      .then((r) => setStreakDismissed(!!r.data?.streak_badge_dismissed))
      .catch(() => {});
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/dashboard');
      setDashboardData(response.data);
      const today = new Date().toDateString();
      const todayActivities =
        response.data.recent_activities?.filter(
          (a) => new Date(a.date).toDateString() === today
        ).length || 0;
      setDailyGoal({ target: 3, completed: todayActivities });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get('/recommendations');
      setRecommendations(response.data.recommendations);
    } catch (error) {
      console.error('Failed to load recommendations');
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('youth.morning');
    if (hour < 17) return t('youth.afternoon');
    return t('youth.evening');
  };

  const formatProtectiveFactor = (factor) =>
    factor.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const getDailyProgress = () =>
    Math.min((dailyGoal.completed / dailyGoal.target) * 100, 100);

  const userStats = {
    breathing_sessions: dashboardData?.total_activities || 0,
    journal_entries: dashboardData?.journal_entries_count || 0,
    creative_activities: Math.floor((dashboardData?.total_activities || 0) * 0.3),
    messages_sent: Math.floor((dashboardData?.total_activities || 0) * 0.1),
    unique_activities: Math.min(dashboardData?.total_activities || 0, 10),
    streak_days: dashboardData?.streak_days || 0,
    total_activities: dashboardData?.total_activities || 0,
  };

  // Daily-rotating featured hero
  const featuredHeroKey = HERO_ORDER[new Date().getDate() % HERO_ORDER.length];
  const featuredHero = HEROES[featuredHeroKey];

  return (
    <div className="bs-shell" data-testid="youth-dashboard">
      <div className="bs-stars" />
      <div className="bs-aurora" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Greeting + daily ring */}
        <div className="mb-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="bs-amber font-semibold uppercase tracking-widest text-xs mb-2">
              <Sparkles className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
              {getGreeting()}
            </p>
            <h1 className="bs-title text-4xl sm:text-5xl font-extrabold leading-tight">
              {currentMessage || t('youth.yourWellnessJourney')}
            </h1>
            <p className="bs-muted mt-3 text-sm sm:text-base">
              {currentTime.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
              {' · '}
              <span className="bs-amber font-semibold">
                <Flame className="inline w-4 h-4 mr-1 -mt-0.5" />
                {dashboardData?.streak_days || 0} {t('youth.daysStreak')}
              </span>
            </p>
          </div>

          {/* Daily goal ring */}
          <div className="flex items-center gap-4 bs-glass-soft px-5 py-4">
            <div className="w-20 h-20 relative">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="44" fill="none" stroke="url(#bsRing)" strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${getDailyProgress() * 2.765} 276.5`}
                  className="transition-all duration-700"
                />
                <defs>
                  <linearGradient id="bsRing" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-lg font-extrabold text-white">{dailyGoal.completed}</div>
                <div className="text-[10px] bs-muted">{t('youth.of')} {dailyGoal.target}</div>
              </div>
            </div>
            <div>
              <p className="text-xs bs-muted uppercase tracking-widest">{t('youth.dailyGoal')}</p>
              <p className="bs-title font-bold">{t('heroLed.yourMomentum')}</p>
            </div>
          </div>
        </div>

        {/* Streak badge + push reminder opt-in */}
        <StreakBadge
          streakDays={dashboardData?.streak_days || 0}
          heroKey={featuredHeroKey}
          initialDismissed={streakDismissed}
        />

        {/* Hero of the Day */}
        <HeroOfTheDayCard heroKey={featuredHeroKey} />

        {/* Three hero paths — Calm-style category tiles */}
        <div className="mb-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h3 className="bs-title text-2xl font-extrabold flex items-center gap-2">
                <Compass className="w-6 h-6 text-amber-300" /> {t('heroLed.explorePaths')}
              </h3>
              <p className="bs-muted text-sm">{t('heroLed.pickAHeroSub')}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {HERO_ORDER.map((hk) => {
              const hero = HEROES[hk];
              const tr = t(`heroes.${hk}`, { returnObjects: true });
              const Icon = HERO_ICONS[hk];
              const count = ACTIVITY_CATALOG[hk].length;
              return (
                <Link key={hk} to={`/activities?hero=${hk}`} data-testid={`hero-path-tile-${hk}`}>
                  <Card className={`bs-glass hero-tile h-full border-0 overflow-hidden`}>
                    <div className={`h-1.5 bg-gradient-to-r ${hero.gradient}`} />
                    <div className="p-5 flex flex-col items-start">
                      <div className={`hero-avatar w-16 h-20 mb-3 ${hero.imageBg}`}>
                        <img src={hero.image} alt={tr.name} />
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-amber-300" />
                        <span className="bs-amber text-xs font-bold uppercase tracking-wider">{tr.pathTitle}</span>
                      </div>
                      <h4 className="bs-title font-extrabold text-lg leading-tight">{tr.name}</h4>
                      <p className="bs-muted text-xs mt-1 line-clamp-2">{tr.pathDesc}</p>
                      <span className="bs-chip mt-4">
                        <Target className="w-3.5 h-3.5" />
                        {t('heroLed.activitiesCount', { n: count })}
                      </span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <div className="-mx-4 sm:mx-0 mb-8 overflow-x-auto no-scrollbar">
            <TabsList
              className="inline-flex w-max sm:w-full sm:grid sm:grid-cols-4 h-auto bg-white/5 border border-white/10 backdrop-blur p-1 mx-4 sm:mx-0"
            >
              <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('youth.tabOverview')}</TabsTrigger>
              <TabsTrigger value="achievements" data-testid="tab-achievements" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('youth.tabAchievements')}</TabsTrigger>
              <TabsTrigger value="recommendations" data-testid="tab-recommendations" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('youth.tabForYou')}</TabsTrigger>
              <TabsTrigger value="progress" data-testid="tab-progress" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('youth.tabProgress')}</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            {/* Stat tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatTile
                label={t('youth.activitiesDone')}
                value={dashboardData?.total_activities || 0}
                icon={<Target className="w-5 h-5" />}
                testId="total-activities-card"
              />
              <StatTile
                label={t('youth.streak')}
                value={dashboardData?.streak_days || 0}
                icon={<Flame className="w-5 h-5" />}
                accent="amber"
                testId="streak-days-card"
              />
              <StatTile
                label={t('youth.journalEntries')}
                value={dashboardData?.journal_entries_count || 0}
                icon={<BookOpen className="w-5 h-5" />}
                testId="journal-entries-card"
              />
              <StatTile
                label={t('youth.avgMood')}
                value={
                  dashboardData?.avg_mood_this_week
                    ? `${(dashboardData.avg_mood_this_week * 100).toFixed(0)}%`
                    : '—'
                }
                icon={<Heart className="w-5 h-5" />}
                accent="amber"
                testId="mood-score-card"
              />
            </div>

            {/* Quick Start row */}
            <Card className="bs-glass border-0 p-6 mb-8" data-testid="quick-actions-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="bs-title text-xl font-extrabold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-300" /> {t('heroLed.quickStart')}
                </h2>
                <Link to="/activities" className="bs-amber text-sm font-semibold flex items-center gap-1">
                  {t('youth.discoverMore')}
                </Link>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {HERO_ORDER.map((hk) => {
                  const hero = HEROES[hk];
                  const tr = t(`heroes.${hk}`, { returnObjects: true });
                  return (
                    <Link
                      key={hk}
                      to={`/activities?hero=${hk}`}
                      data-testid={`quick-start-${hk}`}
                    >
                      <div className={`bs-glass-soft p-4 hover:scale-[1.02] transition-transform`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`hero-avatar w-12 h-14 ${hero.imageBg}`}>
                            <img src={hero.image} alt={tr.name} />
                          </div>
                          <div>
                            <p className="bs-amber text-[10px] uppercase tracking-widest font-bold">
                              {tr.pathTitle}
                            </p>
                            <p className="bs-title font-bold text-sm leading-tight">
                              {t(HERO_QUICK_LABEL_KEY[hk])}
                            </p>
                          </div>
                        </div>
                        <p className="bs-muted text-xs italic">"{tr.tagline}"</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </Card>

            {/* Protective factors */}
            <Card className="bs-glass border-0 p-6" data-testid="protective-factors-section">
              <h2 className="bs-title text-xl font-extrabold mb-5 flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-300" /> {t('youth.yourStrengths')}
              </h2>
              {dashboardData?.protective_factors ? (
                <div className="grid md:grid-cols-2 gap-5">
                  {Object.entries(dashboardData.protective_factors).map(([factor, value]) => (
                    <div key={factor} data-testid={`factor-${factor}`}>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-semibold text-white">
                          {formatProtectiveFactor(factor)}
                        </span>
                        <span className="text-sm bs-muted">{(value * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-300 to-blue-400 transition-all duration-700"
                          style={{ width: `${value * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="bs-muted text-sm">{t('youth.growingStrongerDesc')}</p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="achievements">
            <Card className="bs-glass border-0 p-6">
              <GamifiedProgress
                userStats={userStats}
                onAchievementUnlock={(achievement) => {
                  toast.success(`${t('heroLed.youDidIt')} ${achievement.title}`);
                }}
              />
            </Card>
          </TabsContent>

          <TabsContent value="recommendations">
            <Card className="bs-glass border-0 p-6">
              <h2 className="bs-title text-xl font-extrabold mb-5 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-300" /> {t('youth.recommendedForYou')}
              </h2>
              {recommendations.length > 0 ? (
                <div className="grid gap-4">
                  {recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="bs-glass-soft p-5"
                      data-testid={`recommendation-${index}`}
                    >
                      <h3 className="bs-title font-bold text-lg mb-1">{rec.title}</h3>
                      <p className="bs-muted text-sm mb-3">{rec.description}</p>
                      <Link to="/activities">
                        <button className="bs-cta" data-testid={`try-${rec.activity_type}-btn`}>
                          {t('youth.tryActivity')}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="flex justify-center mb-4">
                    <div className={`hero-avatar w-20 h-24 ${featuredHero.imageBg}`}>
                      <img src={featuredHero.image} alt="" />
                    </div>
                  </div>
                  <p className="bs-muted mb-5">
                    {lang === 'es'
                      ? 'Comienza a escribir en el diario para recibir recomendaciones personalizadas.'
                      : 'Start journaling to get personalized recommendations.'}
                  </p>
                  <Link to="/journal">
                    <button className="bs-cta" data-testid="start-journaling-btn">
                      {t('youth.writeJournal')}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="progress">
            <div className="space-y-6">
              {dashboardData?.recent_activities && dashboardData.recent_activities.length > 0 && (
                <Card className="bs-glass border-0 p-6">
                  <h2 className="bs-title text-xl font-extrabold mb-5 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-300" /> {t('youth.recentActivitiesTitle')}
                  </h2>
                  <div className="space-y-2">
                    {dashboardData.recent_activities.map((activity, index) => (
                      <div key={index} className="bs-glass-soft px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-blue-500 flex items-center justify-center text-blue-950 font-extrabold">
                            {activity.type.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-semibold capitalize text-sm">
                              {activity.type.replace('_', ' ')}
                            </p>
                            <p className="bs-muted text-xs">{activity.date}</p>
                          </div>
                        </div>
                        {activity.duration && (
                          <Badge className="bg-white/10 text-amber-300 border-amber-300/30">
                            {activity.duration}m
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {dashboardData?.mood_trend && dashboardData.mood_trend.length > 0 && (
                <Card className="bs-glass border-0 p-6">
                  <h2 className="bs-title text-xl font-extrabold mb-5">{t('youth.moodTrend')}</h2>
                  <div className="space-y-3">
                    {dashboardData.mood_trend.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between gap-4">
                        <span className="text-sm bs-muted w-28">{entry.date}</span>
                        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-300 to-blue-400 transition-all duration-700"
                            style={{ width: `${entry.mood * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-white w-10 text-right">
                          {(entry.mood * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Companion floats at the bottom of the page */}
        <div className="mt-10">
          <HeroCompanion variant="footer" heroKey={featuredHeroKey} />
        </div>
      </div>
    </div>
  );
};

export default YouthDashboard;
